import React from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown, Tooltip } from "antd";
import { Users, Calendar, FileText, Eye, MoreVertical } from "lucide-react";

const BroadcastIcon = ({ className }) => (
  <svg
    className={className || "tw-size-5"}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9" />
    <path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5" />
    <circle cx="12" cy="12" r="2" fill="currentColor" />
    <path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5" />
    <path d="M19.1 4.9C23 8.8 23 15.2 19.1 19.1" />
  </svg>
);

const ProgressBarMetric = ({ label, value, colorClass, textColorClass }) => {
  const isNA = value === null || value === undefined;
  const displayValue = isNA ? "N/A" : `${value}%`;
  const fillWidth = isNA ? 0 : value;

  return (
    <div className="tw-flex-1 tw-font-inter">
      <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-1.5 tw-mb-1.5">
        <span className="tw-text-slate-500 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider">
          {label}
        </span>
        <span className={isNA ? "tw-text-slate-400 tw-text-xs" : `${textColorClass} tw-font-bold tw-text-xs`}>
          {displayValue}
        </span>
      </div>
      <div className="tw-h-1.5 tw-w-full tw-rounded-full tw-bg-slate-100 tw-overflow-hidden">
        {!isNA && (
          <div
            className={`tw-h-full tw-rounded-full tw-transition-all ${colorClass}`}
            style={{ width: `${fillWidth}%` }}
          />
        )}
      </div>
    </div>
  );
};

const CampaignRow = ({ campaign, onAction }) => {
  const navigate = useNavigate();
  const isPausable = campaign.status === "RUNNING";
  const isResumable = campaign.status === "PAUSED";
  const isEditable = campaign.status === "DRAFT" || campaign.status === "SCHEDULED";
  const isLaunchable = campaign.status === "SCHEDULED";
  
  const campaignRowActions = [
    ...(isEditable ? [{ key: "edit", label: "Edit campaign" }] : []),
    { key: "preview", label: "Preview Template" },
    { key: "stats", label: "View Statistics" },
    { key: "duplicate", label: "Duplicate" },
    ...(isPausable ? [{ key: "pause", label: "Pause Campaign" }] : []),
    ...(isResumable ? [{ key: "resume", label: "Resume Campaign" }] : []),
    ...(isLaunchable ? [{ key: "launch_now", label: "Launch Campaign Now" }] : []),
    { key: "archive", label: "Archive" },
    { key: "delete", label: "Delete Campaign", danger: true },
  ];

  // Determine styling based on status tone
  let iconBgClass = "tw-bg-slate-100 tw-text-slate-600";
  let statusBadge = (
    <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-px-2.5 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-bg-slate-100 tw-text-slate-600 tw-font-inter">
      <span className="tw-size-1.5 tw-rounded-full tw-bg-slate-400" />
      {campaign.status}
    </span>
  );

  if (campaign.statusTone === "sent" || campaign.status === "COMPLETED") {
    iconBgClass = "tw-bg-[#ECFDF5] tw-text-[#007A55]";
    statusBadge = (
      <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-px-2.5 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-bg-[#ECFDF5] tw-text-[#007A55] tw-font-inter">
        <span className="tw-size-1.5 tw-rounded-full tw-bg-[#12B76A]" />
        {campaign.status}
      </span>
    );
  } else if (
    campaign.statusTone === "approval" ||
    campaign.status === "Pending"
  ) {
    iconBgClass = "tw-bg-amber-50 tw-text-amber-700";
    statusBadge = (
      <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-px-2.5 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-bg-amber-50 tw-text-amber-700 tw-font-inter">
        <span className="tw-size-1.5 tw-rounded-full tw-bg-amber-500" />
        {campaign.status}
      </span>
    );
  } else if (campaign.statusTone === "scheduled") {
    iconBgClass = "tw-bg-[#EBF3FF] tw-text-[#2E90FA]";
    statusBadge = (
      <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-px-2.5 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-bg-[#EBF3FF] tw-text-[#2E90FA] tw-font-inter">
        <span className="tw-size-1.5 tw-rounded-full tw-bg-[#2E90FA]" />
        {campaign.status}
      </span>
    );
  }

  return (
    <div className="tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/80 tw-bg-white tw-p-5 tw-shadow-sm hover:tw-border-[#5D44FF]/40 tw-transition-all tw-font-inter">
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
        <div className="tw-flex tw-items-start tw-gap-3.5 tw-min-w-0">
          <div
            className={`tw-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-xl tw-flex-shrink-0 ${iconBgClass}`}
          >
            <BroadcastIcon className="tw-size-5" />
          </div>
          <div className="tw-min-w-0">
            <div className="tw-flex tw-items-center tw-gap-2.5 tw-flex-wrap">
              <h4
                className="tw-text-sm md:tw-text-base tw-font-bold tw-text-slate-800 tw-m-0 tw-truncate tw-cursor-pointer hover:tw-text-[#5D44FF] tw-transition-colors tw-font-inter"
                onClick={() => navigate(`/whatsapp-campaigns/edit/${campaign.id}`, { state: { editCampaign: campaign } })}
              >
                {campaign.name}
              </h4>
              {statusBadge}
            </div>

            <div className="tw-mt-2 tw-flex tw-items-center tw-gap-2.5 tw-text-xs tw-font-medium tw-text-slate-500 tw-flex-wrap tw-font-inter">
              <div className="tw-flex tw-items-center tw-gap-1.5">
                <Users size={13} className="tw-text-slate-400" />
                <span className="tw-font-bold tw-text-slate-700">
                  {campaign.audience}
                </span>
                <span>{campaign.audienceName || "Audience"}</span>
              </div>

              <span className="tw-text-slate-300">•</span>

              <Tooltip title={`Template: ${campaign.template || "Template"} • Click to preview`}>
                <div
                  className="tw-flex tw-items-center tw-gap-1.5 tw-cursor-pointer tw-transition-colors group"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onAction) onAction("preview", campaign);
                  }}
                >
                  <FileText size={13} className="tw-text-slate-400 group-hover:tw-text-[#5D44FF] tw-transition-colors" />
                  <span className="tw-font-medium tw-text-slate-700 group-hover:tw-text-[#5D44FF] tw-transition-colors">
                    {campaign.template || "View Template"}
                  </span>
                  <Eye size={12} className="tw-text-slate-400 group-hover:tw-text-[#5D44FF] tw-transition-colors" />
                </div>
              </Tooltip>

              {campaign.sentAt && (
                <>
                  <span className="tw-text-slate-300">•</span>
                  <div className="tw-flex tw-items-center tw-gap-1.5">
                    <Calendar size={13} className="tw-text-slate-400" />
                    <span>{campaign.sentAt}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <Dropdown
          trigger={["click"]}
          menu={{ 
            items: campaignRowActions,
            onClick: ({ key }) => onAction && onAction(key, campaign)
          }}
          placement="bottomRight"
        >
          <button
            type="button"
            className="tw-border-0 tw-bg-transparent tw-text-slate-400 hover:tw-text-slate-700 tw-cursor-pointer tw-p-1.5 tw-rounded-lg hover:tw-bg-slate-100 tw-flex-shrink-0 tw-transition-colors"
            aria-label="Actions"
          >
            <MoreVertical size={16} />
          </button>
        </Dropdown>
      </div>

      {/* Divider */}
      <div className="tw-mt-4 tw-border-t tw-border-b-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-slate-100" />

      {/* Metrics Progress Row */}
      <div className="tw-mt-4 tw-flex tw-items-center tw-gap-6 tw-flex-wrap sm:tw-flex-nowrap">
        <ProgressBarMetric
          label="DELIVERED"
          value={campaign.delivered}
          colorClass="tw-bg-[#10B981]"
          textColorClass="tw-text-[#10B981]"
        />
        <ProgressBarMetric
          label="READ"
          value={campaign.read}
          colorClass="tw-bg-[#5D44FF]"
          textColorClass="tw-text-[#5D44FF]"
        />
        <ProgressBarMetric
          label="CLICKED"
          value={campaign.clicked}
          colorClass="tw-bg-[#6366F1]"
          textColorClass="tw-text-[#6366F1]"
        />
      </div>
    </div>
  );
};

export default CampaignRow;

